import type { BannerContent } from "./global-banner.types";

/**
 * Gets the appropriate banner content based on current local time and pathname
 * @param now - Current date (defaults to new Date() for easier testing)
 * @param pathname - Current page pathname (optional, used for page-specific banners)
 * @returns Banner content configuration or null if no active promotion
 */
export function getBannerContent(
  now: Date = new Date(),
  pathname?: string,
): BannerContent | null {
  const year = now.getFullYear();
  const currentTime = now.getTime();

  const isInRange = (
    startMonth: number,
    startDay: number,
    startHour: number,
    startMinute: number,
    endMonth: number,
    endDay: number,
    endHour: number,
    endMinute: number,
  ) => {
    const start = new Date(year, startMonth, startDay, startHour, startMinute);
    const end = new Date(year, endMonth, endDay, endHour, endMinute);

    return currentTime >= start.getTime() && currentTime <= end.getTime();
  };

  const isWishlistPage = pathname?.includes("/wishlist");

  // Wishlist Perks: 19th Jan 00:00 - 19th Feb 23:59 (wishlist page only)
  const isWishlistPerksPeriod = isInRange(0, 19, 0, 0, 1, 19, 23, 59);

  // Further Reductions: 15th Jan 00:00 - 18th Jan 23:59
  const isFurtherReductionsPeriod = isInRange(0, 14, 0, 0, 0, 18, 23, 59);

  // Final Reductions: 19th Jan 00:00 - 25th Jan 23:59
  const isFinalReductionsPeriod = isInRange(0, 19, 0, 0, 0, 25, 23, 59);

  // Next Day Delivery: 27th Jan 00:00 - 1st Feb 23:59
  const isNextDayDeliveryPeriod = isInRange(0, 27, 0, 0, 1, 1, 23, 59);

  // Denim Fit Guide: 2nd Feb 00:00 - 8th Feb 23:59
  const isDenimFitGuidePeriod = isInRange(1, 2, 0, 0, 1, 8, 23, 59);

  // Wishlist-specific banner takes priority on wishlist page
  if (isWishlistPage && isWishlistPerksPeriod) {
    return {
      title: "",
      subtitle: "TOPSHOP PERKS: NEXT DAY DELIVERY ONLY £2.95 (ORDERS OVER £40)",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: false,
    };
  }

  if (isFurtherReductionsPeriod) {
    return {
      title: "",
      subtitle: "FURTHER REDUCTIONS - SHOP SALE NOW",
      text: "",
      code: "",
      colorScheme: "red",
      clickable: true,
      href: "/topshop/category/sale?cid=53075",
    };
  }

  if (isFinalReductionsPeriod) {
    return {
      title: "",
      subtitle: "FINAL REDUCTIONS - LAST CHANCE TO SHOP SALE",
      text: "",
      code: "",
      colorScheme: "red",
      clickable: true,
      href: "/topshop/category/sale?cid=53075",
    };
  }

  if (isNextDayDeliveryPeriod) {
    return {
      title: "",
      subtitle: "NEXT DAY DELIVERY ONLY £2.95 (ORDERS OVER £40)",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: false,
    };
  }

  if (isDenimFitGuidePeriod) {
    return {
      title: "",
      subtitle:
        "FIND YOUR PERFECT MATCH - EXPLORE THE DENIM FIT GUIDE AND SHOP LATEST STYLES",
      text: "",
      code: "",
      colorScheme: "black",
      clickable: true,
      href: "/topshop/discover/denim",
    };
  }

  return null;
}
